/**
 * ActorGroupPicker — Multi-select actor picker grouped by Task → Audience → Actor
 *
 * Loads audiences (with taskContext) and actors from Firestore,
 * presents a tree grouped by tasks, allows selecting individual actors.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AudienceService } from '@modules/firebase-core/services/AudienceService';
import type { Audience, Actor } from '@segmently/schema-models/entities';
import { INSIGHTS_MAX_ACTORS_PER_TASK_PACKAGE } from '@modules/shared-types';

export interface ActorSelection {
  actorId: string;
  audienceId: string;
}

export interface ActorGroupLimitViolation {
  taskId: string;
  taskName: string;
  actorCount: number;
  maxActors: number;
}

export interface ActorGroupValidation {
  maxActorsPerTask: number;
  violations: ActorGroupLimitViolation[];
}

export interface ActorGroupPickerProps {
  projectId: string;
  value: ActorSelection[];
  onChange: (selections: ActorSelection[]) => void;
  /** Select all audiences that have actors once the picker data is loaded. */
  selectAllOnLoad?: boolean;
  /** Changes here reset the one-shot auto-selection guard. */
  autoSelectKey?: string | number;
  /** Keep actor rows hidden behind each audience row until the user expands it. */
  collapseActorsByDefault?: boolean;
  /** Prevent selecting more than this many actors for a single task group. */
  maxSelectedActorsPerTask?: number;
  /** Emits current selection validation for parent submit blocking. */
  onValidationChange?: (validation: ActorGroupValidation) => void;
}

interface AudienceWithActors {
  audience: Audience;
  actors: Actor[];
  taskId?: string;
  taskName?: string;
}

interface TaskGroup {
  taskId: string;
  taskName: string;
  audiences: AudienceWithActors[];
}

export const ActorGroupPicker: React.FC<ActorGroupPickerProps> = ({
  projectId,
  value,
  onChange,
  selectAllOnLoad = false,
  autoSelectKey,
  collapseActorsByDefault = false,
  maxSelectedActorsPerTask = INSIGHTS_MAX_ACTORS_PER_TASK_PACKAGE,
  onValidationChange,
}) => {
  const [loading, setLoading] = useState(true);
  const [audiencesWithActors, setAudiencesWithActors] = useState<AudienceWithActors[]>([]);
  const autoSelectedRef = useRef(false);

  // Load audiences with actors
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const service = new AudienceService(projectId);
        const audiences = await service.listWithActorsAndSubcollections();

        if (cancelled) return;

        const result: AudienceWithActors[] = audiences.map(aud => ({
          audience: aud,
          actors: aud.actors ?? [],
          taskId: (aud as any).taskContext?.taskId,
          taskName: (aud as any).taskContext?.taskName,
        }));

        setAudiencesWithActors(result);
      } catch (err) {
        console.error('[ActorGroupPicker] Failed to load audiences:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [projectId]);

  useEffect(() => {
    autoSelectedRef.current = false;
  }, [projectId, autoSelectKey]);

  // Group by task
  const { taskGroups, ungrouped } = useMemo(() => {
    const groups = new Map<string, TaskGroup>();
    const ungrouped: AudienceWithActors[] = [];

    for (const item of audiencesWithActors) {
      if (item.taskId && item.taskName) {
        if (!groups.has(item.taskId)) {
          groups.set(item.taskId, {
            taskId: item.taskId,
            taskName: item.taskName,
            audiences: [],
          });
        }
        groups.get(item.taskId)!.audiences.push(item);
      } else {
        ungrouped.push(item);
      }
    }

    return { taskGroups: [...groups.values()], ungrouped };
  }, [audiencesWithActors]);

  // Selection helpers
  const selectedSet = useMemo(() => {
    return new Set(value.map(s => `${s.audienceId}:${s.actorId}`));
  }, [value]);

  const actorTaskMetaByKey = useMemo(() => {
    const meta = new Map<string, { taskId: string; taskName: string }>();
    for (const item of audiencesWithActors) {
      const audienceId = item.audience.id!;
      const taskId = item.taskId || `audience:${audienceId}`;
      const taskName = item.taskName || item.audience.name || audienceId;
      for (const actor of item.actors) {
        meta.set(`${audienceId}:${actor.id!}`, { taskId, taskName });
      }
    }
    return meta;
  }, [audiencesWithActors]);

  const selectedCountsByTask = useMemo(() => {
    const counts = new Map<string, { taskId: string; taskName: string; actorCount: number }>();
    for (const selection of value) {
      const key = `${selection.audienceId}:${selection.actorId}`;
      const meta = actorTaskMetaByKey.get(key) ?? {
        taskId: `audience:${selection.audienceId}`,
        taskName: selection.audienceId,
      };
      const current = counts.get(meta.taskId) ?? { ...meta, actorCount: 0 };
      current.actorCount += 1;
      counts.set(meta.taskId, current);
    }
    return counts;
  }, [actorTaskMetaByKey, value]);

  const validation = useMemo<ActorGroupValidation>(() => ({
    maxActorsPerTask: maxSelectedActorsPerTask,
    violations: [...selectedCountsByTask.values()]
      .filter((entry) => entry.actorCount > maxSelectedActorsPerTask)
      .map((entry) => ({
        taskId: entry.taskId,
        taskName: entry.taskName,
        actorCount: entry.actorCount,
        maxActors: maxSelectedActorsPerTask,
      })),
  }), [maxSelectedActorsPerTask, selectedCountsByTask]);

  useEffect(() => {
    onValidationChange?.(validation);
  }, [onValidationChange, validation]);

  const cappedSelectionsFromAllActors = useCallback((): ActorSelection[] => {
    const all: ActorSelection[] = [];
    const counts = new Map<string, number>();
    for (const item of audiencesWithActors) {
      const audienceId = item.audience.id!;
      const taskId = item.taskId || `audience:${audienceId}`;
      for (const actor of item.actors) {
        const count = counts.get(taskId) ?? 0;
        if (count >= maxSelectedActorsPerTask) continue;
        all.push({ actorId: actor.id!, audienceId });
        counts.set(taskId, count + 1);
      }
    }
    return all;
  }, [audiencesWithActors, maxSelectedActorsPerTask]);

  const isActorSelected = useCallback((audienceId: string, actorId: string) => {
    return selectedSet.has(`${audienceId}:${actorId}`);
  }, [selectedSet]);

  const toggleActor = useCallback((audienceId: string, actorId: string) => {
    const key = `${audienceId}:${actorId}`;
    if (selectedSet.has(key)) {
      onChange(value.filter(s => !(s.audienceId === audienceId && s.actorId === actorId)));
    } else {
      const meta = actorTaskMetaByKey.get(key) ?? { taskId: `audience:${audienceId}`, taskName: audienceId };
      const count = selectedCountsByTask.get(meta.taskId)?.actorCount ?? 0;
      if (count >= maxSelectedActorsPerTask) return;
      onChange([...value, { actorId, audienceId }]);
    }
  }, [actorTaskMetaByKey, maxSelectedActorsPerTask, onChange, selectedCountsByTask, selectedSet, value]);

  const toggleAudience = useCallback((item: AudienceWithActors) => {
    const audienceActors = item.actors.map(a => ({
      actorId: a.id!,
      audienceId: item.audience.id!,
    }));
    const allSelected = audienceActors.every(s => selectedSet.has(`${s.audienceId}:${s.actorId}`));

    if (allSelected) {
      // Deselect all actors in this audience
      const keysToRemove = new Set(audienceActors.map(s => `${s.audienceId}:${s.actorId}`));
      onChange(value.filter(s => !keysToRemove.has(`${s.audienceId}:${s.actorId}`)));
    } else {
      // Select all actors in this audience
      const existing = new Set(value.map(s => `${s.audienceId}:${s.actorId}`));
      const taskId = item.taskId || `audience:${item.audience.id!}`;
      let remaining = maxSelectedActorsPerTask - (selectedCountsByTask.get(taskId)?.actorCount ?? 0);
      const toAdd = audienceActors.filter((selection) => {
        if (remaining <= 0) return false;
        if (existing.has(`${selection.audienceId}:${selection.actorId}`)) return false;
        remaining -= 1;
        return true;
      });
      onChange([...value, ...toAdd]);
    }
  }, [maxSelectedActorsPerTask, onChange, selectedCountsByTask, selectedSet, value]);

  const selectAll = useCallback(() => {
    onChange(cappedSelectionsFromAllActors());
  }, [cappedSelectionsFromAllActors, onChange]);

  const deselectAll = useCallback(() => {
    onChange([]);
  }, [onChange]);

  useEffect(() => {
    if (!selectAllOnLoad || loading || autoSelectedRef.current || value.length > 0) return;
    const all: ActorSelection[] = [];
    const counts = new Map<string, number>();
    for (const item of audiencesWithActors) {
      if (item.actors.length === 0) continue;
      const taskId = item.taskId || `audience:${item.audience.id!}`;
      for (const actor of item.actors) {
        const count = counts.get(taskId) ?? 0;
        if (count >= maxSelectedActorsPerTask) continue;
        all.push({ actorId: actor.id!, audienceId: item.audience.id! });
        counts.set(taskId, count + 1);
      }
    }
    autoSelectedRef.current = true;
    if (all.length > 0) onChange(all);
  }, [audiencesWithActors, loading, maxSelectedActorsPerTask, onChange, selectAllOnLoad, value.length]);

  // Render audience with its actors
  const renderAudience = (item: AudienceWithActors) => {
    const audienceId = item.audience.id!;
    const totalActors = item.actors.length;
    const selectedCount = item.actors.filter(a => isActorSelected(audienceId, a.id!)).length;
    const allSelected = totalActors > 0 && selectedCount === totalActors;
    const someSelected = selectedCount > 0 && !allSelected;
    const taskId = item.taskId || `audience:${audienceId}`;
    const selectedInTask = selectedCountsByTask.get(taskId)?.actorCount ?? 0;
    const audienceSelectionDisabled = !allSelected && selectedInTask >= maxSelectedActorsPerTask;

    return (
      <Accordion
        key={audienceId}
        disableGutters
        defaultExpanded={!collapseActorsByDefault && totalActors > 0}
        sx={{ ml: 2, mb: 1, '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
          <FormControlLabel
            onClick={(event) => event.stopPropagation()}
            onFocus={(event) => event.stopPropagation()}
            control={
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={() => toggleAudience(item)}
                disabled={audienceSelectionDisabled}
                size="small"
              />
            }
            label={
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" fontWeight={500}>
                  {item.audience.name || 'Unnamed Audience'}
                </Typography>
                <Chip
                  label={`${selectedCount}/${totalActors}`}
                  size="small"
                  sx={{ height: 20 }}
                  color={selectedCount > 0 ? 'primary' : 'default'}
                  variant="outlined"
                />
                {totalActors === 0 && <Chip label="no actors" size="small" variant="outlined" color="warning" />}
              </Stack>
            }
          />
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, pl: 5 }}>
          {item.actors.map(actor => (
            <FormControlLabel
              key={actor.id}
              control={
                <Checkbox
                  checked={isActorSelected(audienceId, actor.id!)}
                  onChange={() => toggleActor(audienceId, actor.id!)}
                  disabled={!isActorSelected(audienceId, actor.id!) && selectedInTask >= maxSelectedActorsPerTask}
                  size="small"
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  {actor.name || 'Unnamed Actor'}
                  {actor.weight ? ` (w: ${actor.weight})` : ''}
                </Typography>
              }
            />
          ))}
          {item.actors.length === 0 && (
            <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
              No actors
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const totalActors = audiencesWithActors.reduce((sum, a) => sum + a.actors.length, 0);

  return (
    <Box>
      <Alert severity={validation.violations.length > 0 ? 'warning' : 'info'} sx={{ mb: 1 }}>
        Per-task aggregation supports up to {maxSelectedActorsPerTask} selected actors per task.
      </Alert>
      {validation.violations.map((violation) => (
        <Typography key={violation.taskId} variant="caption" color="warning.main" sx={{ display: 'block', mb: 0.5 }}>
          {violation.taskName}: {violation.actorCount}/{violation.maxActors} actors selected.
        </Typography>
      ))}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Select Actors ({value.length}/{totalActors})
        </Typography>
        <Stack direction="row" spacing={1}>
          <Chip
            label="Select All"
            size="small"
            onClick={selectAll}
            variant="outlined"
            clickable
          />
          <Chip
            label="Clear"
            size="small"
            onClick={deselectAll}
            variant="outlined"
            clickable
          />
        </Stack>
      </Stack>

      {/* Task groups */}
      {taskGroups.map(group => (
        <Accordion key={group.taskId} defaultExpanded disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">
              Task: {group.taskName}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {group.audiences.map(renderAudience)}
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Ungrouped audiences */}
      {ungrouped.length > 0 && (
        <Accordion defaultExpanded disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" color="text.secondary">
              Ungrouped
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {ungrouped.map(renderAudience)}
          </AccordionDetails>
        </Accordion>
      )}

      {totalActors === 0 && (
        <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: 'center' }}>
          No actors found in this project
        </Typography>
      )}
    </Box>
  );
};
